import { Link as RouterLink } from 'react-router-dom';

import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Pagination,
    Rating,
    Stack,
    Typography
} from '@mui/material';

import {
    formatReviewDate,
    moderationReasonText,
    productPath
} from './reviewViewHelpers';

const MyReviewList = ({
    deletingId,
    error,
    loading,
    onDelete,
    onEdit,
    onPageChange,
    pagination,
    reviews
}) => {
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error)
        return <Alert severity="error">{error}</Alert>;

    if (reviews.length < 1) {
        return (
            <Alert severity="info">
                No tienes reseñas que coincidan con estos filtros.
            </Alert>
        );
    }

    return (
        <>
            <Stack spacing={2}>
                {reviews.map(review => {
                    const path = productPath(review.product);
                    const hidden = review.status === 'hidden';

                    return (
                        <Card
                            id={`review-${review.id}`}
                            key={review.id}
                            variant="outlined"
                        >
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={3} md={2}>
                                        <Box
                                            component="img"
                                            src={review.product.image_url}
                                            alt={review.product.title}
                                            loading="lazy"
                                            sx={{
                                                height: 130,
                                                objectFit: 'contain',
                                                width: '100%'
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={9} md={10}>
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            justifyContent="space-between"
                                            alignItems={{
                                                xs: 'flex-start',
                                                sm: 'center'
                                            }}
                                            spacing={1}
                                        >
                                            <Box>
                                                <Typography
                                                    variant="subtitle1"
                                                    fontWeight="bold"
                                                >
                                                    {review.product.title}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {review.variant?.title ||
                                                        'Presentación única'}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                color={hidden ? 'warning' : 'success'}
                                                label={hidden ? 'Oculta' : 'Publicada'}
                                                size="small"
                                            />
                                        </Stack>

                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={1}
                                            alignItems={{
                                                xs: 'flex-start',
                                                sm: 'center'
                                            }}
                                            sx={{ mt: 1 }}
                                        >
                                            <Rating value={review.rating} readOnly />
                                            {review.verified_purchase &&
                                                <Chip
                                                    color="success"
                                                    label="Compra verificada"
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            }
                                        </Stack>

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ mt: 1 }}
                                        >
                                            Publicada el {
                                                formatReviewDate(review.created_at)
                                            }
                                            {review.edited_at ?
                                                ` · Editada el ${
                                                    formatReviewDate(
                                                        review.edited_at
                                                    )
                                                }`
                                            :
                                                ''}
                                        </Typography>

                                        {hidden &&
                                            <Alert severity="warning" sx={{ mt: 2 }}>
                                                Esta reseña no se muestra en la tienda.
                                                Motivo: <b>{moderationReasonText(
                                                    review.moderation?.reason
                                                )}</b>. Puedes editarla, pero seguirá
                                                oculta hasta que el equipo la restaure.
                                            </Alert>
                                        }

                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 2, whiteSpace: 'pre-wrap' }}
                                        >
                                            {review.comment}
                                        </Typography>

                                        {review.images?.length > 0 &&
                                            <Grid container spacing={1} sx={{ mt: 1 }}>
                                                {review.images.map(image =>
                                                    <Grid
                                                        item
                                                        xs={4}
                                                        sm={3}
                                                        md={2}
                                                        key={image.id}
                                                    >
                                                        <Box
                                                            component="img"
                                                            src={image.url}
                                                            alt={image.alt}
                                                            loading="lazy"
                                                            sx={{
                                                                borderRadius: 1,
                                                                height: 90,
                                                                objectFit: 'cover',
                                                                width: '100%'
                                                            }}
                                                        />
                                                    </Grid>
                                                )}
                                            </Grid>
                                        }

                                        {review.admin_reply &&
                                            <Box sx={{
                                                bgcolor: 'grey.100',
                                                borderLeft: '4px solid',
                                                borderColor: 'primary.main',
                                                borderRadius: 1,
                                                mt: 2,
                                                p: 2
                                            }}>
                                                <Typography variant="subtitle2">
                                                    {review.admin_reply.author}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        mt: 0.5,
                                                        whiteSpace: 'pre-wrap'
                                                    }}
                                                >
                                                    {review.admin_reply.text}
                                                </Typography>
                                                {hidden &&
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        display="block"
                                                        sx={{ mt: 1 }}
                                                    >
                                                        Esta respuesta tampoco se muestra
                                                        públicamente mientras la reseña
                                                        permanezca oculta.
                                                    </Typography>
                                                }
                                            </Box>
                                        }
                                    </Grid>
                                </Grid>
                            </CardContent>
                            <CardActions sx={{ px: 2, pb: 2 }}>
                                <Button
                                    onClick={() => onEdit(review)}
                                    disabled={
                                        deletingId === review.id ||
                                        !review.variant
                                    }
                                >
                                    Editar
                                </Button>
                                <Button
                                    color="error"
                                    onClick={() => onDelete(review)}
                                    disabled={deletingId === review.id}
                                >
                                    {deletingId === review.id ?
                                        'Eliminando...'
                                    :
                                        'Eliminar'}
                                </Button>
                                {path &&
                                    <Button component={RouterLink} to={path}>
                                        Ver producto
                                    </Button>
                                }
                            </CardActions>
                        </Card>
                    );
                })}
            </Stack>
            {pagination.pages > 1 &&
                <Stack alignItems="center" sx={{ mt: 3 }}>
                    <Pagination
                        count={pagination.pages}
                        page={pagination.page}
                        onChange={(event, page) => onPageChange(page)}
                    />
                </Stack>
            }
        </>
    );
};

export default MyReviewList;
